/**
 * Client emitter for the two D1 events.
 *
 * Telemetry must never break the app: every failure path here is swallowed.
 * The queue is bounded — an offline tab must not grow memory without limit.
 */
const ENDPOINT = "/api/v1/telemetry/page-events";
const MAX_BATCH = 50;

type ExitType = "navigation" | "close" | "hidden";
interface QueuedEvent {
  event_id: string;
  event_name: "page_view" | "page_exit";
  route_template: string;
  occurred_at: string;
  [k: string]: unknown;
}

let queue: QueuedEvent[] = [];

function newId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Distinct from the old "cf_session_id", which belonged to a legacy
// analytics/track.ts (deleted: it was dead code with no importers). That
// module rotated its key against a companion "cf_session_last" TTL marker
// this module never writes, so a shared key would have let two rotation
// policies fight over one storage slot — splitting a single visit into
// several session_ids and inflating M-03's denominator. The module is gone,
// but the distinct name stays: it is already persisted in live browsers, and
// renaming it now would orphan in-flight sessions for no gain.
const SESSION_KEY = "cf_nav_session_id";

function sessionId(): string | undefined {
  try {
    let v = window.sessionStorage.getItem(SESSION_KEY);
    if (!v) { v = newId(); window.sessionStorage.setItem(SESSION_KEY, v); }
    return v;
  } catch { return undefined; }
}

function deviceClass(): string {
  if (typeof window === "undefined") return "unknown";
  const w = window.innerWidth;
  return w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop";
}

function push(e: QueuedEvent): void {
  if (queue.length >= MAX_BATCH) queue.shift(); // bounded: drop oldest
  queue.push(e);
}

function base(name: QueuedEvent["event_name"], route: string): QueuedEvent {
  return {
    event_id: newId(),
    event_name: name,
    route_template: route,
    occurred_at: new Date().toISOString(),
    session_id: sessionId(),
    device_class: deviceClass(),
    locale: typeof document !== "undefined" ? document.documentElement.lang : undefined,
    release_sha: import.meta.env.VITE_GIT_SHA ?? undefined,
  };
}

export function emitPageView(route: string, extra: Record<string, unknown> = {}): void {
  push({ ...base("page_view", route), ...extra });
}

export function emitPageExit(route: string, engagedMs: number, exitType: ExitType): void {
  push({ ...base("page_exit", route), engaged_ms: Math.max(0, Math.round(engagedMs)), exit_type: exitType });
}

export async function flushPageEvents(): Promise<void> {
  if (queue.length === 0) return;
  const events = queue.splice(0, MAX_BATCH);
  try {
    await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events }),
      keepalive: true,
    });
  } catch {
    /* lossy by design — never surface a telemetry failure to the user */
  }
}

/** Final lifecycle flush. sendBeacon survives unload where fetch may not. */
export function beaconFlush(): void {
  if (queue.length === 0) return;
  const events = queue.splice(0, MAX_BATCH);
  try {
    const blob = new Blob([JSON.stringify({ events })], { type: "application/json" });
    if (!navigator.sendBeacon?.(ENDPOINT, blob)) void flushPageEvents();
  } catch { /* lossy */ }
}

/** Test-only. */
export function __resetQueue(): void { queue = []; }
